import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import Image from '../../components/image/Image';
import Button from '../../components/button/Button';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { getRiskMetricsProtectors } from '../../services/GuaranteesService';
import { getProtectorColumns, ProtectorRow } from '../../utils/protectorColumns';
import { routes } from '../../routes/routePaths';

interface ProtectorTableData {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const ProtectorSection = () => {
  const navigate = useNavigate();
  const [protectors, setProtectors] = useState<ProtectorRow[]>([]);
  const [tableData, setTableData] = useState<ProtectorTableData>({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    field: 'amount' | 'integrated' | null;
    direction: 'asc' | 'desc' | null;
  }>({
    field: null,
    direction: null,
  });

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRequest = useRef(0);

  useEffect(() => {
    fetchProtectors();
  }, [page, sortConfig.field, sortConfig.direction]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      if (page === 0) {
        fetchProtectors(0);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchText]);

  const fetchProtectors = async (customPage?: number) => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    try {
      const response = await getRiskMetricsProtectors(
        customPage ?? page,
        tableData.size,
        undefined,
        sortConfig.field || undefined,
        sortConfig.direction || undefined,
        searchText
      );

      if (lastRequest.current !== requestId) return;

      if (response && response.success && (response as any).data && Array.isArray((response as any).data.content)) {
        const pageResponse: any = (response as any).data;

        const mappedProtectors: ProtectorRow[] = pageResponse.content.map(
          (item: any) => ({
            id: item.id,
            name: item.name,
            cuit: item.cuit,
            amount: item.amount,
            integrated: item.integrated,
            percentage: item.percentage,
          })
        );

        setProtectors(mappedProtectors);
        setTableData({
          page: pageResponse.page ?? 0,
          size: pageResponse.size && pageResponse.size > 0 ? pageResponse.size : tableData.size || 20,
          totalElements: pageResponse.totalElements ?? mappedProtectors.length,
          totalPages: pageResponse.totalPages ?? 1,
        });
      } else {
        setProtectors([]);
        setTableData({
          page: 0,
          size: tableData.size || 20,
          totalElements: 0,
          totalPages: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching protectors:', error);
      setProtectors([]);
      setTableData({
        page: 0,
        size: tableData.size || 20,
        totalElements: 0,
        totalPages: 0,
      });
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    if (field !== 'amount' && field !== 'integrated') {
      return;
    }

    let newDirection: 'asc' | 'desc' = 'asc';

    if (direction === 'asc' || direction === 'desc') {
      newDirection = direction;
    }

    setSortConfig({ field, direction: newDirection });
    setPage(0);
  };

  const handleRowClicked = (row: ProtectorRow) => {
    if (row.id) {
      navigate(routes.protectorDetail.replace(':id', row.id.toString()));
    }
  };

  const columns = getProtectorColumns(handleRowClicked).map((column) => {
    if (column.sortField === 'amount' || column.sortField === 'integrated') {
      return {
        ...column,
        sortable: true,
        sortField: column.sortField,
        onSort: undefined,
      };
    }
    return column;
  });

  const handleSearch = () => {
    setIsSearching(true);
    setPage(0);
    fetchProtectors(0);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card display='flex' justifyContent='space-between' alignItems='center'>
        <H type='2' weight='bold' modifier='strong'>
          Protectores
        </H>
        <Button
          variant='primary'
          onClick={() => navigate(routes.protectorNew)}
        >
          Nuevo protector
        </Button>
      </Card>

      <Card display='flex' gap='16px' alignItems='center'>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar por razón social o documento'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={(e: any) => e.keyCode === 13 && handleSearch()}
          />
        </Card>
      </Card>

      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : protectors.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={protectors}
            tableData={tableData}
            handleChangePage={handleChangePage}
            onSort={handleSort}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay protectores para mostrar.
          </P>
          <P variant='primary' align='center'>
            Ajustá los filtros o intentá más tarde.
          </P>
        </Card>
      )}
    </div>
  );
};

export default ProtectorSection;


